import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  Inject,
  TrackByFunction
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Store } from "@ngxs/store";
import { TagsState } from "@vp/data-access/tags";
import { PricingPlanOverride, Tag } from "@vp/models";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

export interface DialogData {
  tagData: Readonly<Record<string, unknown>>;
}

@Component({
  selector: "vp-add-new-specialty-dialog",
  standalone: false,
  templateUrl: "./add-new-specialty-dialog.component.html",
  styleUrls: ["./add-new-specialty-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddNewSpecialtyDialogComponent {
  tags$: Observable<Tag[]> = inject(Store).select(TagsState.tags);

  form!: UntypedFormGroup;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private readonly dialogRef: MatDialogRef<AddNewSpecialtyDialogComponent>,
    readonly formBuilder: UntypedFormBuilder
  ) {
    this.form = formBuilder.group({
      specialtyTagId: [null, Validators.required]
    });
  }

  specialties$ = this.tags$.pipe(
    map((tags: Tag[]) => {
      const tagData = this.data.tagData;
      if (
        Object.keys(tagData).includes("serviceCostOverrides") &&
        Array.isArray(tagData.serviceCostOverrides)
      ) {
        const overrides = tagData.serviceCostOverrides as PricingPlanOverride[];
        return tags.filter(
          (tag: Tag) =>
            tag.tagTypeFriendlyId === "specialty" &&
            !overrides
              .reduce((accumulator: string[], item: PricingPlanOverride) => {
                accumulator = accumulator.concat(item.specialties);
                return accumulator;
              }, [])
              .includes(tag.tagId)
        );
      }
      return [];
    })
  );

  @HostListener("window:keyup.esc") onKeyUp() {
    console.log("keyup");
  }

  @HostListener("window:beforeunload", ["$event"]) unloadHandler(event: Event) {
    event.returnValue = false;
  }

  onCancel = () => {
    this.close(false);
  };

  onSave = () => {
    this.close(true);
  };

  close(save: boolean) {
    if (save && this.form.valid) {
      this.dialogRef.close(this.form.getRawValue().specialtyTagId);
    } else {
      if (this.form.pristine || this.form.invalid) {
        this.dialogRef.close();
      } else {
        if (confirm("Are you sure you want to cancel and potentially lose data?")) {
          this.dialogRef.close();
        }
      }
    }
  }

  trackById: TrackByFunction<Tag> = (_index: number, item: Tag) => item.tagId;
}
